import { useState } from "react";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import {
  useUploadBayHireAttachmentMetadata,
  useUploadBayHireReceipt,
} from "@features/bay-hire/hooks/useBayHireMutations";
import {
  makeReceiptUploadDialogSchema,
  receiptUploadDialogDefaultValues,
  type ReceiptUploadDialogFormData,
} from "@features/bay-hire/schema/receiptUploadDialogSchema";
import { validateBayHireAttachmentFile } from "@features/bay-hire/utils/bayHireFileValidation";
import { getApiErrorDetail } from "@features/bay-hire/utils/getApiErrorDetail";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  bayHireService,
  type StagedAttachment,
  type UploadReceiptRequest,
} from "@services/bayHireApi";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface UseReceiptUploadDialogProps {
  open: boolean;
  requestId: string;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
}

interface UseReceiptUploadDialogResult {
  form: ReturnType<typeof useForm<ReceiptUploadDialogFormData>>;
  receiptStagedAttachment: StagedAttachment | null;
  receiptFileName: string | null;
  submitError: string | null;
  isSubmitting: boolean;
  isReceiptStaging: boolean;
  files: UploadEntry[];
  setFiles: (files: UploadEntry[]) => void;
  handleReceiptUpload: (selected: File[]) => Promise<void>;
  handleRemoveReceipt: () => void;
  handleSubmitForm: (data: ReceiptUploadDialogFormData) => Promise<void>;
  /** DFT-0106: wraps the raw `onClose` prop with the staged-state reset below - see that note. */
  handleClose: () => void;
}

/**
 * DFT-0014 (F-04): Vendor payment-receipt upload dialog logic. Reuses the
 * DFT-0013 two-phase upload pattern (usePreApproveDialog's
 * handleHodFileSelected) - attachmentType="PAYMENT_RECEIPT" instead of
 * "HOD_APPROVAL".
 *
 * DFT-0103: the receipt is now staged only (blob-only, no backend attach call) on drop, and
 * registered as a durable per-request attachment only at submit time - mirroring
 * `BayHireCreateAttachmentUpload.tsx`'s own stage-then-register pattern. Previously this called
 * the eager `useUploadBayHireAttachment` hook, durably persisting the receipt attachment the
 * instant a file was dropped; abandoning the drawer left an orphaned row behind that was returned
 * by the request's own GET-by-id endpoint but never displayed (masked, not prevented, by
 * DFT-0088's read-side exclusion rule).
 */
export const useReceiptUploadDialog = (
  props: UseReceiptUploadDialogProps,
): UseReceiptUploadDialogResult => {
  const { requestId, onClose, onSuccess } = props;
  const { t } = useTranslation();
  // DFT-0044 (FE-13/FE-70): same profile the create form's own Attachments upload already
  // enforces - see bayHireFileValidation.ts. DFT-0088 addendum (2026-09-01): widened from
  // pdf/jpg/png-only to pdf/jpg/jpeg/png/bmp/gif plus Excel/Word, 10 MB - see
  // FileUpload.constants.ts's `bayHireAttachment` profile.
  const uploadConditions = useFileUploadProfiles().bayHireAttachment;
  const [receiptStagedAttachment, setReceiptStagedAttachment] = useState<StagedAttachment | null>(
    null,
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [files, setFiles] = useState<UploadEntry[]>([]);
  const [isReceiptStaging, setIsReceiptStaging] = useState(false);

  const uploadReceiptMutation = useUploadBayHireReceipt();
  const registerAttachmentMutation = useUploadBayHireAttachmentMetadata();
  const receiptFileName = receiptStagedAttachment?.originalFileName ?? null;

  const form = useForm<ReceiptUploadDialogFormData>({
    resolver: zodResolver(makeReceiptUploadDialogSchema(t)),
    defaultValues: receiptUploadDialogDefaultValues,
  });

  // DFT-0106: this used to be a `useEffect` keyed on `open` transitioning to `false` - but this
  // dialog's real parent (`BayHireRequestDetail.tsx`) mounts/unmounts it entirely per row
  // (`{openDrawers.receiptUpload && <ReceiptUploadDialog open .../>}`, `open` a literal `true`
  // at every real call site), so `open` never actually transitions to `false` on a persistently-
  // mounted instance - that effect was dead code in production. Reset is now explicit, called
  // from `handleClose` (wraps the raw `onClose` prop) and after a successful submit, mirroring
  // `ReasonDialog.tsx`'s own already-correct pattern.
  const resetStagedReceipt = () => {
    setReceiptStagedAttachment(null);
    setSubmitError(null);
    setFiles([]);
    form.reset(receiptUploadDialogDefaultValues);
    useUploadStore.getState().clearFilesForKey("PAYMENT_RECEIPT");
  };

  const handleClose = () => {
    resetStagedReceipt();
    onClose();
  };

  // AC19/AC21 (DFT-0050): rebuilt on the shared `FileUpload` component (AC24) - `onUpload`
  // hands back the dropzone's accepted files, matching `ReasonDialog.handleUpload`'s shape.
  //
  // DFT-0103: stages the blob only (SAS direct-to-Azure, no backend attach call) - the durable
  // per-request registration is deferred to `handleSubmitForm`, matching
  // `BayHireCreateAttachmentUpload.tsx`'s own `handleUpload`.
  const handleReceiptUpload = async (selected: File[]) => {
    const file = selected[selected.length - 1];
    if (!file) return;

    const validation = validateBayHireAttachmentFile(file, uploadConditions);
    if (!validation.ok) {
      setFiles([]);
      setSubmitError(
        validation.reason === "size"
          ? t("bay-hire.receipt-upload.file-too-large")
          : t("bay-hire.receipt-upload.unsupported-file-type", {
              formats: uploadConditions.format?.join(", "),
              defaultValue: `Unsupported file type. Allowed: ${uploadConditions.format?.join(", ")}.`,
            }),
      );
      return;
    }

    setSubmitError(null);
    setIsReceiptStaging(true);
    try {
      const staged = await bayHireService.uploadStagedAttachment(file, "PAYMENT_RECEIPT");
      setReceiptStagedAttachment(staged);
      // DFT-0096: overwrite FileUpload's own auto-synced entry (the raw,
      // not-meaningfully-serializable File object) with the real uploaded
      // reference, mirroring SingleCertificate.tsx's onSuccess - name/url,
      // not path/relativePath, is what should survive a refresh.
      useUploadStore.getState().setFilesForKey("PAYMENT_RECEIPT", [
        {
          name: staged.originalFileName ?? file.name,
          url: staged.documentUrl ?? "",
          attachmentType: "PAYMENT_RECEIPT",
        },
      ]);
    } catch (error) {
      console.error("Failed to upload payment receipt document:", error);
      setFiles([]);
      useUploadStore.getState().clearFilesForKey("PAYMENT_RECEIPT");
      setSubmitError(t("bay-hire.receipt-upload.upload-error"));
    } finally {
      setIsReceiptStaging(false);
    }
  };

  // AC19/AC21 (DFT-0050): the remove control - clears the staged receipt so the dropzone
  // re-enables and a new document can be selected. Since no backend call was ever made for a
  // staged-only attachment (DFT-0103), there is nothing to clean up server-side and no orphaned
  // row is left behind - unlike the pre-DFT-0103 eager-persist behaviour this replaces.
  const handleRemoveReceipt = () => {
    setReceiptStagedAttachment(null);
    setFiles([]);
    setSubmitError(null);
    useUploadStore.getState().clearFilesForKey("PAYMENT_RECEIPT");
  };

  const handleSubmitForm = async (data: ReceiptUploadDialogFormData) => {
    if (!receiptStagedAttachment) {
      setSubmitError(t("bay-hire.receipt-upload.missing-attachment"));
      return;
    }

    setSubmitError(null);
    try {
      // DFT-0103: a freshly staged receipt has no `id` yet - register it durably against the
      // live request now, immediately before the real `uploadReceipt` call, exactly like
      // `useBayHireContainer.tsx`'s `handleUpdateFormSubmit`/`handleResubmitFormSubmit`.
      let attachmentId: string;
      if (!receiptStagedAttachment.id) {
        const registered = await registerAttachmentMutation.mutateAsync({
          id: requestId,
          request: {
            attachmentType: receiptStagedAttachment.attachmentType,
            documentUrl: receiptStagedAttachment.documentUrl,
            originalFileName: receiptStagedAttachment.originalFileName,
            contentType: receiptStagedAttachment.contentType,
            fileSizeBytes: receiptStagedAttachment.fileSizeBytes,
          },
        });
        attachmentId = registered.id;
      } else {
        attachmentId = receiptStagedAttachment.id;
      }

      const request: UploadReceiptRequest = {
        receiptNumber: data.receiptNumber,
        receiptDate: data.receiptDate,
        receiptAmount: Number(data.receiptAmount),
        attachmentId,
      };
      await uploadReceiptMutation.mutateAsync({ id: requestId, request });
      // DFT-0106: same reasoning as handleClose - the dialog is unmounted right after this by
      // its parent, not toggled `open={false}`, so this is the only reliable place left to clear
      // staged state before it could otherwise leak into the next request this dialog opens for.
      resetStagedReceipt();
      await onSuccess();
    } catch (error) {
      console.error("Receipt upload submission failed:", error);
      // DFT-0077 (AD-5): prefer the backend's own explanation over the generic message.
      setSubmitError(getApiErrorDetail(error, t("bay-hire.receipt-upload.submit-error")));
    }
  };

  return {
    form,
    receiptStagedAttachment,
    receiptFileName,
    submitError,
    isSubmitting: registerAttachmentMutation.isPending || uploadReceiptMutation.isPending,
    isReceiptStaging,
    files,
    setFiles,
    handleReceiptUpload,
    handleRemoveReceipt,
    handleSubmitForm,
    handleClose,
  };
};
